import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/client.js";
import { useI18n } from "../i18n/I18nContext.jsx";
import Dropdown from "../components/Dropdown.jsx";
import "../styles/Analytics.css";

export default function Analytics() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("resolution_rate");

  useEffect(() => {
    api
      .get("/analytics/officers/")
      .then((r) => setOfficers(r.data.officers || []))
      .finally(() => setLoading(false));
  }, []);

  const sorted = [...officers].sort((a, b) => {
    if (sortBy === "resolution_rate") return b.resolution_rate - a.resolution_rate;
    if (sortBy === "total_paras") return b.total_paras - a.total_paras;
    if (sortBy === "avg_days") return a.avg_days_to_close - b.avg_days_to_close;
    return 0;
  });

  const topPerformer = sorted.filter((o) => o.resolution_rate > 0)[0];
  const totalOfficers = officers.length;
  const avgResolutionRate = officers.length
    ? (officers.reduce((s, o) => s + o.resolution_rate, 0) / officers.length).toFixed(1)
    : 0;

  const getRateClass = (rate) => {
    if (rate >= 75) return "rate-high";
    if (rate >= 40) return "rate-mid";
    return "rate-low";
  };

  if (loading) {
    return (
      <div className="center-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-analytics">
      <div className="page-header">
        <div>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="stat-grid">
        <div className="stat-card" data-testid="kpi-total-officers">
          <div className="stat-value">{totalOfficers}</div>
          <div className="stat-label">{t("activeOfficers")}</div>
        </div>
        <div className="stat-card green" data-testid="kpi-avg-rate">
          <div className="stat-value">{avgResolutionRate}%</div>
          <div className="stat-label">{t("avgResolutionRate")}</div>
        </div>
        <div className="stat-card" data-testid="kpi-top-performer">
          <div className="stat-value" style={{ fontSize: 18 }}>
            {topPerformer?.full_name || "—"}
          </div>
          <div className="stat-label">
            {topPerformer
              ? `${t("topPerformer")} (${topPerformer.resolution_rate}%)`
              : t("noClosedParas")}
          </div>
        </div>
      </div>

      {/* Sort Toolbar */}
      <div className="toolbar">
        <Dropdown
          value={sortBy}
          onChange={(v) => setSortBy(v)}
          placeholder={t("sortByLabel")}
          options={[
            { value: "resolution_rate", label: t("sortByRateDesc") },
            { value: "total_paras", label: t("sortByTotalDesc") },
            { value: "avg_days", label: t("sortByAvgDaysAsc") },
          ]}
        />
      </div>

      {/* Officers Table */}
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t("officer")}</th>
                <th>{t("zone")}</th>
                <th>{t("department")}</th>
                <th className="t-right">{t("total")}</th>
                <th className="t-right">{t("resolved")}</th>
                <th className="t-right">{t("statusPENDING")}</th>
                <th className="t-right">{t("avgDays")}</th>
                <th>{t("resolutionRate")}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={9} className="empty-state">
                    {t("noOfficerData")}
                  </td>
                </tr>
              )}
              {sorted.map((o, idx) => (
                <tr
                  key={o.id}
                  onClick={() => navigate(`/officers/${o.id}`)}
                  data-testid={`analytics-row-${o.id}`}
                >
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {idx === 0 && o.resolution_rate > 0 && (
                        <span title="Top performer" style={{ fontSize: 18 }}>🏆</span>
                      )}
                      <div>
                        <strong>{o.full_name}</strong>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {o.designation || o.role_display}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{o.zone_name || "—"}</td>
                  <td>{o.department || "—"}</td>
                  <td className="t-right"><strong>{o.total_paras}</strong></td>
                  <td className="t-right" style={{ color: "var(--green)" }}>
                    {o.resolved}
                  </td>
                  <td className="t-right">{o.pending}</td>
                  <td className="t-right">{o.avg_days_to_close}</td>
                  <td style={{ minWidth: 180 }}>
                    <div className="rate-bar">
                      <div
                        className={"rate-fill " + getRateClass(o.resolution_rate)}
                        style={{ width: `${o.resolution_rate}%` }}
                      />
                      <span className="rate-text">{o.resolution_rate}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
